import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Col, DatePicker, Form, Row, Select, Tooltip } from 'antd';
import moment from 'moment';
import styled from 'styled-components';

import { Layout } from '../layouts';
import { ACCOUNT_MNG_MENU } from '../layouts/AccountManager';
import { FormItemProps } from 'app/components/FormContainer';
import {
  selectDistrict,
  selectInfoUser,
  selectNational,
  selectProvince,
  selectTranslations,
} from 'app/pages/App/slice/selectors';
import { ReactComponent as CalendarIcon } from 'app/assets/img/calendar.svg';
import { ReactComponent as ArrowDown } from 'app/assets/img/arrow_down.svg';
import { objectType } from 'types';
import { FormContainerWrapper } from 'app/components/NewFormContainer/styled';
import StyledButton from 'app/components/StyledButton';
import InputText from 'app/components/FormInput/InputText';
import { useGlobalFunction } from 'app/pages/App/hooks/useGlobalFunction';
import { updateMeApi } from 'app/services/authApi';
import {
  GET_DISTRICT,
  GET_NATIONAL,
  GET_PROVINCE,
  appActions,
} from 'app/pages/App/slice';
import ExitConfirm from 'app/components/ExitConfirm';
import { LoadingContainer } from 'app/components/LoadingContainer';
import useWindowSize from 'app/hooks/useWindowSize';
import Revenue from './components/Revenue';
import { NATIONAL_ID, SYSTEM_MAINTENANCE } from 'utils/constants';
import { I_Address } from 'app/pages/App/slice/types';

type Address = { id: number; label: string };

const PersonalInfo = () => {
  const [loading, setLoading] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const { onOpenDialogNoti, onCloseDialogNoti } = useGlobalFunction();
  const translations = useSelector(selectTranslations);
  const infoUser = useSelector(selectInfoUser);
  const nationals = useSelector(selectNational);
  const provinces = useSelector(selectProvince);
  const districts = useSelector(selectDistrict);
  const dispatch = useDispatch();
  const { device } = useWindowSize();

  const LIST_GENDER = useMemo(
    () => [
      { id: 'male', label: translations?.['b2b.gender.male'] ?? '' },
      { id: 'female', label: translations?.['b2b.gender.female'] ?? '' },
    ],
    [translations],
  );

  const mapAddressData = useCallback((data: I_Address[]) => {
    return (
      data?.map(i => ({
        id: i.id,
        label: i.name,
      })) ?? []
    );
  }, []);

  const listNational = useMemo(
    () => mapAddressData(nationals ?? []),
    [mapAddressData, nationals],
  );

  const [listProvince, setListProvince] = useState<Address[]>([]);
  useEffect(() => {
    setListProvince(mapAddressData(provinces ?? []));
  }, [mapAddressData, provinces]);

  const [listDistrict, setListDistrict] = useState<Address[]>([]);
  useEffect(() => {
    setListDistrict(mapAddressData(districts ?? []));
  }, [districts, mapAddressData]);

  const [form] = Form.useForm();

  const initInfoUser = useMemo(
    () => ({
      name: infoUser?.name,
      phone: infoUser?.phone,
      email: infoUser?.username,
      address: infoUser?.address,
      gender: infoUser?.gender || null,
      dob: infoUser?.birthdate && moment(infoUser?.birthdate, 'DD-MM-YYYY'),
      // nation: infoUser?.nation?.id ?? null,
      // province: infoUser?.province?.id ?? null,
      // district: infoUser?.district?.id ?? null,
    }),
    [infoUser],
  );

  const national = Form.useWatch('nation', form);
  const province = Form.useWatch('province', form);

  const onTriggerSubmit = useCallback(() => {
    form.submit();
  }, [form]);

  const onSubmitForm = async (values: objectType) => {
    setLoading(true);
    try {
      const payload = { ...values };
      const formData = new FormData();
      Object.entries(payload).forEach(([key, value]) => {
        if (key === 'email' || key === 'name') {
        } else if (key === 'dob') {
          if (value) formData.append(key, moment(value).format('yyyy-MM-DD'));
        } else if (key === 'gender') {
          if (value) formData.append(key, value);
        } else formData.append(key, value);
      });
      // if (national !== NATIONAL_ID.VIET_NAM) {
      //   formData.append('province', 'null');
      //   formData.append('district', 'null');
      // }
      const { data } = await updateMeApi(formData);

      dispatch(appActions.setInfoUser(data));
      setIsFormDirty(false);

      onOpenDialogNoti({
        type: 'SUCCESS',
        isShow: true,
        title: translations?.['b2b.message.updatePersonInfoSuccess'] ?? '',
        content: '',
        closable: true,
        onCloseDialog: onCloseDialogNoti,
        maskClosable: false,
      });
    } catch (err) {
      if (err === SYSTEM_MAINTENANCE) setIsFormDirty(false);
    } finally {
      setLoading(false);
    }
  };

  const resetUserInfo = () => {
    if (!loading) {
      form.setFieldsValue({
        ...initInfoUser,
        phone: '',
        address: '',
        gender: 'male',
        dob: '',
        // nation: null,
        // province: null,
        // district: null,
      });
      setIsFormDirty(true);
    }
  };

  const LIST_FORM_FIELDS: FormItemProps[] = useMemo(
    () => [
      {
        label: translations ? translations['b2b.form.name'] : '',
        name: 'name',
        rules: [{ required: true }],
        validateTrigger: ['onBlur'],
        useTooltipValue: true,
        children: (
          <InputText
            size="large"
            onPressEnter={onTriggerSubmit}
            placeholder={translations?.['b2b.form.namePlaceholder'] ?? ''}
            disabled
            style={{ fontStyle: 'italic', color: '#D9D9D9' }}
          />
        ),
      },
      {
        label: translations ? translations['b2b.form.phone'] : '',
        name: 'phone',
        rules: [
          { min: 10 },
          { max: 20 },
          {
            pattern: /^[0-9]+$/g,
            message: translations?.['b2b.form.wrongPhoneFormat'] ?? '',
          },
        ],
        validateTrigger: ['onBlur'],
        children: (
          <InputText
            size="large"
            onPressEnter={onTriggerSubmit}
            placeholder={translations?.['b2b.form.phone'] ?? ''}
            onlyNumber
            maxLength={20}
            style={{ fontStyle: 'italic', color: '#808080' }}
            placeholderStyle={{ color: '#808080', opacity: 1 }}
          />
        ),
      },
      {
        label: translations?.['b2b.form.email'] ?? '',
        name: 'email',
        rules: [{ required: true }, { type: 'email' }],
        validateTrigger: ['onBlur'],
        useTooltipValue: true,
        children: (
          <InputText
            size="large"
            onPressEnter={onTriggerSubmit}
            placeholder={translations?.['b2b.form.email']}
            disabled
            style={{ fontStyle: 'italic', color: '#D9D9D9' }}
          />
        ),
      },
      {
        label: translations ? translations['b2b.form.gender'] : '',
        name: 'gender',
        validateTrigger: ['onBlur'],
        children: (
          <StyledSelect
            suffixIcon={<ArrowDown />}
            placeholder={translations?.['b2b.form.genderPlaceholder'] ?? ''}
            style={{ fontStyle: 'italic' }}
          >
            {LIST_GENDER.map(item => (
              <Select.Option key={item.id} value={item.id}>
                {item.label}
              </Select.Option>
            ))}
          </StyledSelect>
        ),
      },
      {
        label: translations ? translations['b2b.form.birth'] : '',
        name: 'dob',
        validateTrigger: ['onBlur'],
        children: (
          <StyledDatePicker
            placeholder={translations?.['b2b.form.dobPlaceholder'] ?? ''}
            format={['DD/MM/YYYY']}
            size="large"
            suffixIcon={<CalendarIcon />}
            disabledDate={current => current && current.valueOf() > Date.now()}
            style={{ fontSize: 14, fontStyle: 'italic' }}
            allowClear={false}
          />
        ),
      },
      // {
      //   label: translations ? translations['b2b.form.national'] : '',
      //   name: 'nation',
      //   validateTrigger: ['onBlur'],
      //   children: (
      //     <StyledSelect
      //       suffixIcon={<ArrowDown />}
      //       placeholder={translations?.['b2b.form.national'] ?? ''}
      //       style={{ fontStyle: 'italic' }}
      //       onChange={() => {
      //         form.setFieldsValue({ province: null, district: null });
      //         setListProvince([]);
      //         setListDistrict([]);
      //       }}
      //     >
      //       {listNational.map(item => (
      //         <Select.Option key={item.id} value={item.id}>
      //           {item.label}
      //         </Select.Option>
      //       ))}
      //     </StyledSelect>
      //   ),
      // },
      // {
      //   label: translations ? translations['b2b.form.city'] : '',
      //   name: 'province',
      //   validateTrigger: ['onBlur'],
      //   enable: !national || national === NATIONAL_ID.VIET_NAM,
      //   children: (
      //     <StyledSelect
      //       suffixIcon={<ArrowDown />}
      //       placeholder={translations?.['b2b.form.city'] ?? ''}
      //       style={{ fontStyle: 'italic' }}
      //       onChange={() => {
      //         form.setFieldsValue({ district: null });
      //         setListDistrict([]);
      //       }}
      //     >
      //       {listProvince.map(item => (
      //         <Select.Option key={item.id} value={item.id}>
      //           {item.label}
      //         </Select.Option>
      //       ))}
      //     </StyledSelect>
      //   ),
      // },
      // {
      //   label: translations ? translations['b2b.form.district'] : '',
      //   name: 'district',
      //   validateTrigger: ['onBlur'],
      //   enable: !national || national === NATIONAL_ID.VIET_NAM,
      //   children: (
      //     <StyledSelect
      //       suffixIcon={<ArrowDown />}
      //       placeholder={translations?.['b2b.form.district'] ?? ''}
      //       style={{ fontStyle: 'italic' }}
      //     >
      //       {listDistrict.map(item => (
      //         <Select.Option key={item.id} value={item.id}>
      //           {item.label}
      //         </Select.Option>
      //       ))}
      //     </StyledSelect>
      //   ),
      // },
      {
        label: translations ? translations['b2b.form.address'] : '',
        name: 'address',
        rules: [{ max: 200 }],
        validateTrigger: ['onBlur'],
        // span: 24,
        children: (
          <InputText
            size="large"
            onPressEnter={onTriggerSubmit}
            placeholder={translations?.['b2b.form.address'] ?? ''}
            trim
            maxLength={200}
            style={{ fontStyle: 'italic', color: '#808080' }}
            placeholderStyle={{ color: '#808080', opacity: 1 }}
          />
        ),
      },
    ],
    [LIST_GENDER, onTriggerSubmit, translations],
  );

  useEffect(() => {
    const fillFormData = () => {
      form.setFieldsValue(initInfoUser);
    };

    fillFormData();
  }, [initInfoUser, form]);

  // useEffect(() => {
  //   dispatch(GET_NATIONAL());
  // }, [dispatch]);

  // useEffect(() => {
  //   const getProvinces = () => {
  //     if (!national || national !== NATIONAL_ID.VIET_NAM) {
  //       return false;
  //     }
  //     dispatch(GET_PROVINCE(national));
  //   };
  //   getProvinces();
  // }, [dispatch, form, national]);

  // useEffect(() => {
  //   const getDistricts = () => {
  //     if (!province || !national || national !== NATIONAL_ID.VIET_NAM) {
  //       return false;
  //     }
  //     dispatch(GET_DISTRICT(province));
  //   };
  //   getDistricts();
  // }, [dispatch, form, national, province]);

  return (
    <Layout
      title={translations?.['b2b.menu.account'] ?? ''}
      menuActive={ACCOUNT_MNG_MENU.PERSONAL_INFO}
      preElement={<Revenue />}
    >
      <ExitConfirm
        message={translations?.['b2b.txt.confirmExists']}
        enable={isFormDirty}
      />
      <LoadingContainer loading={loading} minHeight="auto">
        <StyledFormContainerWrapper>
          <Form
            form={form}
            validateMessages={{
              required:
                '${label} ' +
                (translations ? translations['b2b.form.validate-require'] : ''),
              string: {
                len: '${label} phải có ${len} kí tự',
                min:
                  '${label} ' +
                  (translations
                    ? translations['b2b.form.validate-min-chars-1']
                    : '') +
                  ' ${min} ' +
                  (translations ? translations['b2b.form.validate-chars'] : ''),
                max:
                  '${label} ' +
                  (translations
                    ? translations['b2b.form.validate-max-chars-1']
                    : '') +
                  ' ${max} ' +
                  (translations ? translations['b2b.form.validate-chars'] : ''),
                range: '${label} must be between ${min} and ${max} kí tự',
              },
              types: {
                email:
                  '${label}: ${type} ' +
                  (translations ? translations['b2b.form.invalid'] : ''),
                number:
                  '${label}: ${type} ' +
                  (translations ? translations['b2b.form.invalid'] : ''),
                string:
                  '${label}: ${type} ' +
                  (translations ? translations['b2b.form.invalid'] : ''),
                date:
                  '${label}: ${type} ' +
                  (translations ? translations['b2b.form.invalid'] : ''),
                method:
                  '${label}: ${type} ' +
                  (translations ? translations['b2b.form.invalid'] : ''),
                array:
                  '${label}: ${type} ' +
                  (translations ? translations['b2b.form.invalid'] : ''),
                object:
                  '${label}: ${type} ' +
                  (translations ? translations['b2b.form.invalid'] : ''),
                boolean:
                  '${label}: ${type} ' +
                  (translations ? translations['b2b.form.invalid'] : ''),
                integer:
                  '${label}: ${type} ' +
                  (translations ? translations['b2b.form.invalid'] : ''),
                float:
                  '${label}: ${type} ' +
                  (translations ? translations['b2b.form.invalid'] : ''),
                regexp:
                  '${label}: ${type} ' +
                  (translations ? translations['b2b.form.invalid'] : ''),
                url:
                  '${label}: ${type} ' +
                  (translations ? translations['b2b.form.invalid'] : ''),
                hex:
                  '${label}: ${type} ' +
                  (translations ? translations['b2b.form.invalid'] : ''),
              },
              number: {
                len: '${label} must equal ${len}',
                min: '${label} cannot be less than ${min}',
                max: '${label} cannot be greater than ${max}',
                range: '${label} must be between ${min} and ${max}',
              },
              array: {
                len: '${label} must be exactly ${len} in length',
                min: '${label} cannot be less than ${min} in length',
                max: '${label} cannot be greater than ${max} in length',
                range: '${label} must be between ${min} and ${max} in length',
              },
              pattern: {
                mismatch: '${label} does not match pattern ${pattern}',
              },
              enum: '${label} must be one of [${enum}]',
              whitespace: '${label} cannot be empty',
              date: {
                format: '${label} is invalid for format date',
                parse: '${label} could not be parsed as date',
                invalid: '${label} is invalid date',
              },
            }}
            onFinish={onSubmitForm}
            onValuesChange={() => setIsFormDirty(true)}
          >
            <Row gutter={device === 'desktop' ? 44 : 49}>
              {LIST_FORM_FIELDS.map((field, index) => {
                const {
                  children,
                  span,
                  enable,
                  useTooltipValue,
                  ...fieldProps
                } = field;
                if (typeof enable === 'boolean' && !enable) return null;
                return (
                  children && (
                    <Col
                      span={span ?? device === 'mobile' ? 24 : 12}
                      key={index}
                    >
                      <Tooltip
                        title={
                          useTooltipValue
                            ? form.getFieldValue(fieldProps.name ?? '')
                            : null
                        }
                      >
                        <Form.Item
                          key={field.name || new Date().toString()}
                          {...fieldProps}
                        >
                          {children}
                        </Form.Item>
                      </Tooltip>
                    </Col>
                  )
                );
              })}
            </Row>
            <Row>
              <ActionButton>
                <StyledButton
                  textColor="#808080"
                  border="solid 1px #808080"
                  bgColor="#fff"
                  onClick={resetUserInfo}
                  style={{ fontSize: 16 }}
                >
                  {translations?.['b2b.btn.reType']}
                </StyledButton>
                <StyledButton
                  textColor="#fff"
                  onClick={onTriggerSubmit}
                  disabled={loading}
                  style={{ fontSize: 16 }}
                >
                  {translations?.['b2b.btn.saveInfo']}
                </StyledButton>
              </ActionButton>
            </Row>
          </Form>
        </StyledFormContainerWrapper>
      </LoadingContainer>
    </Layout>
  );
};

export default PersonalInfo;

const StyledFormContainerWrapper = styled(FormContainerWrapper)`
  overflow-x: hidden;
  .ant-input,
  .ant-picker,
  .ant-select-single:not(.ant-select-customize-input) .ant-select-selector {
    border: none;
    border-radius: 0 !important;
    border-bottom: solid 1px #d9d9d9;
  }
  .ant-input {
    &[disabled] {
      background: #fff;
    }
  }
  .ant-col {
    label {
      display: none;
    }
  }
`;

const ActionButton = styled.div`
  margin-top: 24px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  & > *:not(:first-child) {
    margin-left: 20px;
    @media (max-width: 1279px) {
      margin-left: 38px;
    }
    @media (max-width: 540px) {
      margin-left: 20px;
    }
  }
  .ant-btn:hover, .ant-btn:focus {
    color
  }
  @media (max-width: 1279px) {
    margin-top: 8px;
    button {
      width: 50%;
    }
  }
`;

const StyledDatePicker = styled(DatePicker)`
  .ant-picker-input {
    & > input {
      font-style: italic;
      font-size: 14px;
      color: #808080;
      ::placeholder {
        color: #808080;
        opacity: 1;
      }
    }
  }
`;

const StyledSelect = styled(Select)`
  .ant-select-selection-item,
  .ant-select-selection-placeholder {
    color: #808080;
  }
`;
